import i18n from '../i18n'
import {
  isStripeModeMismatchError,
  sanitizeClientBillingMessage,
  subscriptionCheckoutCancelUrl,
  subscriptionCheckoutSuccessUrl,
  subscriptionService,
} from '../services/subscriptionService'
import { redirectForRole } from './completeGoogleSignIn'

const PENDING_PLUS_CHECKOUT_KEY = 'zimark_pending_plus_checkout'
const PENDING_PLAN_KEY = 'zimark_pending_checkout_plan'
/** Abandoned checkout flags must not trap logged-in users on marketing `/`. */
const PENDING_MAX_AGE_MS = 10 * 60 * 1000

export type PendingCheckoutPlan = 'plus' | 'pro'

export function setPendingPlusCheckout(plan: PendingCheckoutPlan = 'plus') {
  try {
    sessionStorage.setItem(PENDING_PLUS_CHECKOUT_KEY, String(Date.now()))
    sessionStorage.setItem(PENDING_PLAN_KEY, plan)
  } catch {
    /* ignore */
  }
}

export function clearPendingPlusCheckout() {
  try {
    sessionStorage.removeItem(PENDING_PLUS_CHECKOUT_KEY)
    sessionStorage.removeItem(PENDING_PLAN_KEY)
  } catch {
    /* ignore */
  }
}

export function hasPendingPlusCheckout(): boolean {
  try {
    const raw = sessionStorage.getItem(PENDING_PLUS_CHECKOUT_KEY)
    if (raw == null) return false
    // Legacy flag from older builds
    if (raw === '1') {
      sessionStorage.setItem(PENDING_PLUS_CHECKOUT_KEY, String(Date.now()))
      return true
    }
    const startedAt = Number(raw)
    if (!Number.isFinite(startedAt)) {
      clearPendingPlusCheckout()
      return false
    }
    if (Date.now() - startedAt > PENDING_MAX_AGE_MS) {
      clearPendingPlusCheckout()
      return false
    }
    return true
  } catch {
    return false
  }
}

export function getPendingCheckoutPlan(): PendingCheckoutPlan {
  try {
    const plan = sessionStorage.getItem(PENDING_PLAN_KEY)
    return plan === 'pro' ? 'pro' : 'plus'
  } catch {
    return 'plus'
  }
}

function extractApiDetail(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const msg = (detail as { message?: unknown }).message
    if (typeof msg === 'string' && msg.trim()) return msg
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && typeof (first as { msg?: unknown }).msg === 'string') {
      return (first as { msg: string }).msg
    }
  }
  const message = (error as { message?: unknown })?.message
  return typeof message === 'string' && message.trim() ? message : null
}

function checkoutErrorMessage(error: unknown): string {
  const t = i18n.getFixedT(null, 'marketing')
  if (isStripeModeMismatchError(error)) return t('plans.checkoutFailed')
  const raw = extractApiDetail(error)
  if (!raw) return t('plans.checkoutFailed')
  return sanitizeClientBillingMessage(raw, t('plans.checkoutFailed'))
}

/** After student sign-in/register: open Stripe if they chose Plus/Pro on the landing page. */
export async function resumeAfterStudentAuth(role: string): Promise<void> {
  if (role !== 'student' || !hasPendingPlusCheckout()) {
    redirectForRole(role)
    return
  }

  const plan = getPendingCheckoutPlan()
  // Keep the pending flag until we have a Checkout URL so Home does not bounce
  // the just-logged-in student to /messages during the async startCheckout call.
  try {
    const url = await subscriptionService.startCheckout({
      successUrl: subscriptionCheckoutSuccessUrl(),
      cancelUrl: subscriptionCheckoutCancelUrl(),
      plan,
    })
    clearPendingPlusCheckout()
    window.location.href = url
  } catch (error: unknown) {
    clearPendingPlusCheckout()
    console.error('Checkout after auth failed:', error)
    if (isStripeModeMismatchError(error)) {
      try {
        const url = await subscriptionService.startCheckout({
          successUrl: subscriptionCheckoutSuccessUrl(),
          cancelUrl: subscriptionCheckoutCancelUrl(),
          plan,
        })
        window.location.href = url
        return
      } catch (retryErr) {
        console.error('Checkout retry after billing reset failed:', retryErr)
        window.location.href = `/account-settings?tab=subscription&checkout_error=${encodeURIComponent(
          checkoutErrorMessage(retryErr)
        )}`
        return
      }
    }
    // Prefer Account Settings notice over alert() for billing setup issues.
    window.location.href = `/account-settings?tab=subscription&checkout_error=${encodeURIComponent(
      checkoutErrorMessage(error)
    )}`
  }
}

/** Logged-in student on landing — go straight to Stripe Plus. */
export async function startPlusCheckoutFromLanding(): Promise<void> {
  const url = await subscriptionService.startCheckout({
    successUrl: subscriptionCheckoutSuccessUrl(),
    cancelUrl: subscriptionCheckoutCancelUrl(),
    plan: 'plus',
  })
  window.location.href = url
}

/** Logged-in student on landing — go straight to Stripe Pro. */
export async function startProCheckoutFromLanding(): Promise<void> {
  const url = await subscriptionService.startCheckout({
    successUrl: subscriptionCheckoutSuccessUrl(),
    cancelUrl: subscriptionCheckoutCancelUrl(),
    plan: 'pro',
  })
  window.location.href = url
}
